import { useRef } from 'react';
import {
  Box, Button, Typography, Chip,
} from '@mui/material';
import TooltipIconButton from '../../components/TooltipIconButton';
import { Add, Edit, Delete } from '@mui/icons-material';
import { DataGridPremium, GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid-premium';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { tagsService, contrastText } from '../../services/tags.service';
import { useModalState } from '../../hooks/useModalState';
import { useServerPagination } from '../../hooks/useServerPagination';
import { useGridHeight } from '../../hooks/useGridHeight';

export default function TagsPage() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { setModal } = useModalState('tag');

  const filterRef = useRef<HTMLDivElement>(null);
  const gridHeight = useGridHeight(filterRef);
  const { gridProps, offset, limit } = useServerPagination();

  const { data, isFetching } = useQuery({
    queryKey: ['tags', offset, limit],
    queryFn: () => tagsService.query({ offset, limit }),
    placeholderData: (prev) => prev,
  });

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Delete tag "${name}"? It will be removed from all offers, networks, traffic sources, flows and landing pages.`)) return;
    try {
      await tagsService.delete(id);
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      enqueueSnackbar('Tag deleted', { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.error?.message || 'Delete failed', { variant: 'error' });
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'name', headerName: 'Tag', flex: 1,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Chip
            label={params.row.name}
            size="small"
            sx={{ bgcolor: params.row.color, color: contrastText(params.row.color || '#607d8b'), fontWeight: 500 }}
          />
        </Box>
      ),
    },
    { field: 'color', headerName: 'Color', width: 120 },
    {
      field: 'created_at', headerName: 'Created', width: 120,
      valueFormatter: (value) => new Date(value).toLocaleDateString(),
    },
    {
      field: 'actions', headerName: '', width: 90, sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 0, alignItems: 'center', height: '100%' }}>
          <TooltipIconButton size="small" onClick={() => setModal({ open: true, id: params.row.id, reason: 'Update' })} title="Edit">
            <Edit sx={{ fontSize: 16 }} />
          </TooltipIconButton>
          <TooltipIconButton size="small" color="error" onClick={() => handleDelete(params.row.id, params.row.name)} title="Delete">
            <Delete sx={{ fontSize: 16 }} />
          </TooltipIconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box ref={filterRef}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" fontWeight={600}>Tags</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => setModal({ open: true, reason: 'Create' })}>
          Add Tag
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Tag offers, networks, traffic sources, flows and landing pages, then filter the tracking report and conversions by tag.
      </Typography>

            </Box>

      <DataGridPremium
        rows={data?.data || []}
        columns={columns}
        loading={isFetching}
        {...gridProps(data?.total)}
        onRowDoubleClick={(params: GridRowParams, event: React.MouseEvent) => {
          const target = event.target as HTMLElement;
          if (target.closest('[data-field="actions"]')) return;
          setModal({ open: true, id: params.row.id, reason: 'Update' });
        }}
        sx={{ bgcolor: 'background.paper', borderRadius: 2, height: gridHeight }}
      />
    </Box>
  );
}
